/**
 * Renders the open request aimed at this viewer; a control that renders is one core will
 * accept. `key={request.id}` gives a new ask a fresh draft, while a re-emitted id keeps it.
 */

import type { Json, RequestId, RequestView } from '@gsf/contract';
import { rendererFor } from './registry';
import type { NameOf } from './renderer';

export interface PlayerInputProps {
  /** One open request aimed at this viewer. `null` renders nothing. */
  request: RequestView | null;
  onSubmit: (requestId: RequestId, value: Json) => void;
  /** For the kinds that carry participant ids. `kit/roster.ts` builds one. */
  nameOf?: NameOf;
  /** Bigger, for a screen across the room. */
  size?: 'md' | 'lg';
}

const NO_NAMES: NameOf = () => null;

export function PlayerInput({ request, onSubmit, nameOf = NO_NAMES, size = 'md' }: PlayerInputProps) {
  if (!request) return null;

  const Renderer = rendererFor(request.spec.kind);
  if (!Renderer) {
    // Only reachable with a client older than the server. Say so rather than go blank.
    return <p className="error-text">This screen cannot show a “{request.spec.kind}” yet.</p>;
  }

  return (
    <>
      <Renderer
        key={request.id}
        request={request}
        spec={request.spec}
        submit={(value) => onSubmit(request.id, value)}
        nameOf={nameOf}
        disabled={request.disabled !== null}
        size={size}
      />
      {/* An unexplained dead control reads as a bug. */}
      {request.disabled ? <p className="play-hint">{request.disabled.message}</p> : null}
    </>
  );
}
